import { useEffect, useState } from 'react';
import { useRoute, useSession, PlusIcon, PencilIcon, TrashIcon } from '@huaca/ui';
import { archiveTemplate, listTemplates } from '../api';
import type { Template } from '../types';
import { Skeleton } from '../Skeleton';

/** Template management (Editor): list, create, edit, archive. Reached from the dashboard. */
export function TemplateList() {
  const { client, can } = useSession();
  const { navigate } = useRoute();
  const [templates, setTemplates] = useState<Template[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mayEdit = can('claro:template:edit');

  useEffect(() => {
    let live = true;
    listTemplates(client)
      .then((t) => live && setTemplates(t))
      .catch(() => live && setError('לא ניתן לטעון תבניות.'));
    return () => {
      live = false;
    };
  }, [client]);

  async function archive(id: string) {
    await archiveTemplate(client, id);
    setTemplates((ts) => ts?.filter((t) => t._id !== id) ?? null);
  }

  return (
    <div className="claro-page" data-testid="template-list">
      <div className="claro-page-head">
        <h1>תבניות</h1>
        <div className="claro-head-actions">
          <button type="button" onClick={() => navigate('/')}>
            חזרה
          </button>
          {mayEdit && (
            <button type="button" className="btn-primary" onClick={() => navigate('/templates/new')} data-testid="create-template">
              <PlusIcon />
              תבנית חדשה
            </button>
          )}
        </div>
      </div>

      <section>
        {error && <p role="alert">{error}</p>}
        {!templates ? (
          <Skeleton lines={4} />
        ) : templates.length === 0 ? (
          <div className="huaca-state" data-testid="no-templates">
            <h2>אין עדיין תבניות</h2>
            <p>{mayEdit ? 'צרו תבנית ראשונה כדי להתחיל לקלוט לקוחות.' : 'בקשו מעורך בארגון ליצור תבנית.'}</p>
            {mayEdit && (
              <button type="button" className="btn-primary" onClick={() => navigate('/templates/new')}>
                צור תבנית
              </button>
            )}
          </div>
        ) : (
          <ul className="claro-template-list">
            {templates.map((t) => (
              <li key={t._id} data-testid={`template-${t._id}`}>
                <span className="claro-tpl-name">{t.name}</span>
                <span className="claro-tpl-meta">{t.sections.length} מקטעים</span>
                <span className="claro-tpl-spacer" />
                {mayEdit && (
                  <button type="button" onClick={() => navigate(`/templates/${t._id}`)}>
                    <PencilIcon className="huaca-icon--sm" />
                    עריכה
                  </button>
                )}
                {mayEdit && (
                  <button type="button" className="btn-danger" onClick={() => void archive(t._id)}>
                    <TrashIcon className="huaca-icon--sm" />
                    לארכיון
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
